import { useRef, useState, useEffect } from 'react';

interface UseMenuPointerEventsProps {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    disabled?: boolean;
}

/**
 * Custom hook to handle menu pointer events
 * Prevents menus from opening on pointerDown and instead opens on pointerUp
 * to avoid accidental menu item activation when the menu opens at the same position.
 * see https://github.com/radix-ui/primitives/issues/1912 and related issues
 */
export const useMenuPointerEvents = ({
    open: controlledOpen,
    onOpenChange,
    disabled
}: UseMenuPointerEventsProps) => {
    // Use internal state for uncontrolled menus
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;

    // Use ref to track current open state to avoid stale closures
    const openRef = useRef(open);
    openRef.current = open;

    // Track if pointerDown was prevented to know if we should handle pointerUp
    const pointerDownPreventedRef = useRef(false);
    // Track if we're in the process of closing to prevent reopening
    const isClosingRef = useRef(false);
    // Track if component is mounted to prevent state updates after unmount
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleOpenChange = (newOpen: boolean) => {
        if (!isControlled && isMountedRef.current) {
            setInternalOpen(newOpen);
        }
        openRef.current = newOpen;
        isClosingRef.current = false;
        onOpenChange?.(newOpen);
    };

    const handlePointerDown = (e: React.PointerEvent) => {
        if (disabled) {
            return;
        }

        // Only prevent pointerDown when menu is closed to avoid opening on mousedown
        // When menu is open, let Radix handle closing naturally by not preventing
        if (!openRef.current) {
            e.preventDefault();
            pointerDownPreventedRef.current = true;
        } else {
            // Menu is open - let Radix close it, but track that we're closing
            pointerDownPreventedRef.current = false;
            isClosingRef.current = true;
        }
    };

    const handlePointerUp = (e: React.PointerEvent) => {
        if (disabled) {
            return;
        }

        // If we're in the process of closing (menu was open on pointerDown),
        // prevent pointerUp to avoid Radix toggling it back open
        if (isClosingRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        // Only manually open if menu is closed and we prevented pointerDown
        if (pointerDownPreventedRef.current && !openRef.current) {
            e.preventDefault();
            handleOpenChange(true);
            pointerDownPreventedRef.current = false;
        }
    };

    return {
        open,
        handleOpenChange,
        handlePointerDown,
        handlePointerUp
    };
};
